import { useEffect, useState } from 'react'
import { useSearch, useNavigate, Link } from '@tanstack/react-router'
import { Loader2, CheckCircle, AlertCircle, Lock } from 'lucide-react'
import { BrandLogo } from '../components/BrandLogo'

interface TokenInfo {
  email: string
  name: string
  orgName: string
  orgSlug: string
}

export function CompleteRegistrationPage() {
  const navigate = useNavigate()
  const searchParams = useSearch({ from: '/complete-registration' })
  const token = searchParams.token as string | undefined
  
  const [tokenInfo, setTokenInfo] = useState<TokenInfo | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [submitError, setSubmitError] = useState('')
  
  useEffect(() => {
    if (!token) {
      setError('Missing registration token')
      setIsLoading(false)
      return
    }
    
    validateToken()
  }, [token])
  
  const validateToken = async () => {
    try {
      const response = await fetch(`/api/v1/auth/complete-registration/${token}`)
      const data = await response.json()
      
      if (!response.ok) {
        setError(data.error || 'Invalid or expired link')
        return
      }
      
      setTokenInfo(data)
    } catch (err) {
      setError('Failed to validate registration link')
    } finally {
      setIsLoading(false)
    }
  }
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (password.length < 8) {
      setSubmitError('Password must be at least 8 characters')
      return
    }
    
    if (password !== confirmPassword) {
      setSubmitError('Passwords do not match')
      return
    }
    
    setSubmitStatus('loading')
    setSubmitError('')
    
    try {
      const response = await fetch('/api/v1/auth/complete-registration', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ token, password }),
      })
      
      const data = await response.json()
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to complete registration')
      }
      
      setSubmitStatus('success')
      
      // Redirect to dashboard after a short delay
      setTimeout(() => {
        navigate({ to: '/dashboard' })
      }, 2000)
    } catch (err: any) {
      setSubmitStatus('error')
      setSubmitError(err.message || 'Failed to complete registration')
    }
  }
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }
  
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Invalid Link</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <Link
            to="/login"
            className="inline-block px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Go to Login
          </Link>
        </div>
      </div>
    )
  }
  
  if (submitStatus === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
          <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Registration Complete!</h1>
          <p className="text-gray-600 mb-4">
            Welcome to {tokenInfo?.orgName}! You're now signed in.
          </p>
          <p className="text-sm text-gray-500">Redirecting to dashboard...</p>
        </div>
      </div>
    )
  }
  
  return (
    <div className="auth-page min-h-screen flex items-center justify-center bg-background px-4 py-12">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block mb-6">
            <BrandLogo size="lg" />
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">Complete your registration</h1>
          <p className="text-gray-600 mt-2">
            Set a password to finish creating your account
          </p>
        </div>
        
        {/* Card */}
        <div className="bg-white py-4">
          {/* User info */}
          {tokenInfo && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="space-y-2 text-sm">
                <div className="flex flex-wrap justify-between gap-2 break-all">
                  <span className="text-gray-500">Name</span>
                  <span className="font-medium text-gray-900">{tokenInfo.name}</span>
                </div>
                <div className="flex flex-wrap justify-between gap-2 break-all">
                  <span className="text-gray-500">Email</span>
                  <span className="font-medium text-gray-900">{tokenInfo.email}</span>
                </div>
                <div className="flex flex-wrap justify-between gap-2 break-words">
                  <span className="text-gray-500">Organisation</span>
                  <span className="font-medium text-gray-900">{tokenInfo.orgName}</span>
                </div>
              </div>
            </div>
          )}
          
          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Create Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="At least 8 characters"
                />
              </div>
            </div>
            
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                Confirm Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={8}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Confirm your password"
                />
              </div>
            </div>
            
            {submitStatus === 'error' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                {submitError}
              </div>
            )}
            
            <button
              type="submit"
              disabled={submitStatus === 'loading'}
              className="w-full py-3 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {submitStatus === 'loading' ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Creating Account...
                </>
              ) : (
                'Complete Registration'
              )}
            </button>
          </form>
        </div>
        
        {/* Footer */}
        <p className="text-center text-sm text-gray-500 mt-6">
          Already have an account?{' '}
          <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  )
}
